// Pure Chargebee subscription.status classification — no React, no module
// aliases — so the React hooks (subscriptionAccess.ts), the nag banner, the
// lockout gate, and unit tests can all share one definition of what each
// status means. If these sets drift, paying owners get locked out or ended
// subscriptions keep access, so they're deliberately kept in one tiny file.

// Installers that bundle the Powston subscription into their own offering.
// Their customers are billed via the installer, not Powston directly, so
// they're never nagged and never locked out.
export const SUBSCRIPTION_EXEMPT_INSTALLER_IDS = new Set<number>([37, 50]);

export type SubscriptionSite = {
  owner_user_id?: number | null;
  owner_email?: string | null;
  installer_id?: number | null;
};

export type SubscriptionOwnership = {
  ownsSite: boolean;
  exemptByInstaller: boolean;
  // Whether the /api/sites lookup actually produced an answer. The nag banner
  // fails open on a failed lookup — better to stay quiet than to nag someone
  // wrongly — and `ownsSite: false` already achieves that. But anything that
  // *sells* something has to fail closed instead, so it needs to tell
  // "confirmed not exempt" apart from "could not check": a failed lookup
  // otherwise looks exactly like a self-serve customer who owes us money.
  known: boolean;
};

// Legacy duplicate-user records can authenticate as one user id while the
// installer-created site belongs to another row with the same email. Treat
// either identity match as ownership, but do not let unrelated shared sites
// grant an installer exemption to the whole account.
export function classifySubscriptionSites(
  userId: number,
  userEmail: string | null | undefined,
  sites: SubscriptionSite[],
): SubscriptionOwnership {
  const email = (userEmail ?? '').trim().toLowerCase();
  const ownedSites = sites.filter(site => {
    if (site?.owner_user_id === userId) return true;
    const ownerEmail = (site?.owner_email ?? '').trim().toLowerCase();
    return email !== '' && ownerEmail === email;
  });

  return {
    known: true,
    ownsSite: ownedSites.length > 0,
    exemptByInstaller: ownedSites.some(
      site => typeof site?.installer_id === 'number'
        && SUBSCRIPTION_EXEMPT_INSTALLER_IDS.has(site.installer_id),
    ),
  };
}

// Statuses that mean the subscription has ended. An owner in one of these
// states (on a non-exempt site) has lost access.
const TERMINATED_STATUSES = new Set(['cancelled', 'transferred']);

// Statuses that count as a live or pending-live subscription — nothing to
// nag about or block.
const ACTIVE_LIKE_STATUSES = new Set([
  'active', 'in_trial', 'non_renewing', 'paused', 'future',
]);

export function isActiveLikeStatus(status?: string | null): boolean {
  return ACTIVE_LIKE_STATUSES.has((status ?? '').toLowerCase());
}

export function isTerminatedStatus(status?: string | null): boolean {
  return TERMINATED_STATUSES.has((status ?? '').toLowerCase());
}

// What the profile page's Billing section may offer. Powston-installed (50)
// and other bundled-installer (37) owners were still being shown the plan
// picker — and its controller-bundled checkout — because the picker keyed
// off the user's Chargebee status alone. A survey-only customer with no
// subscription record therefore looked identical to a lapsed self-serve
// customer. The nag banner already suppresses on ownership; this puts the
// same rule in front of the checkout button.
//
//   manage      — a live-ish subscription exists: portal link only
//   checking    — the owner needs a plan but ownership isn't known yet; show
//                 nothing purchasable until it is (no flash of the picker)
//   unavailable — the ownership lookup did not return an answer. This must
//                 fail *closed*: the fail-open `{ ownsSite: false,
//                 exemptByInstaller: false }` that /api/sites errors produce
//                 is indistinguishable from a genuine self-serve customer, so
//                 treating it as one would put the controller-bundled
//                 checkout back in front of a bundled-installer owner exactly
//                 when we cannot tell. "Cannot tell" must not become "sell".
//   bundled     — owner of an exempt-installer site who has *no subscription
//                 record at all*: the installer arranges the Powston service,
//                 so there is nothing to buy. Deliberately limited to that
//                 case. A `cancelled` / `transferred` status means the person
//                 did hold a subscription and went through the cancel-service
//                 path, and `useSubscriptionAccess` locks those users out of
//                 the data pages on the status alone — no ownership
//                 exemption. If `bundled` also swallowed a terminated status,
//                 /profile would say "nothing further is needed" while the
//                 gate showed the cancellation screen and offered no way
//                 back. The two surfaces have to agree, so a terminated
//                 status always keeps its reactivation route.
//   resubscribe — plan picker / self-service offer as before
export type BillingOffer =
  | 'manage' | 'checking' | 'unavailable' | 'bundled' | 'resubscribe';

export function resolveBillingOffer(args: {
  status?: string | null;
  hasSubscriptionId: boolean;
  ownership: SubscriptionOwnership | null;
}): BillingOffer {
  const status = (args.status ?? '').toLowerCase();
  const noSub = !args.hasSubscriptionId && !status;
  if (!isTerminatedStatus(status) && !noSub) return 'manage';
  // Only an owner with no subscription record can be bundled — the survey-only
  // customer this whole path exists for. A terminated status goes straight to
  // the reactivation route without consulting ownership at all, which also
  // means a broken /api/sites can never strand a locked-out user on
  // `unavailable` with no way to buy their access back.
  if (!noSub) return 'resubscribe';
  if (args.ownership === null) return 'checking';
  if (!args.ownership.known) return 'unavailable';
  if (args.ownership.exemptByInstaller) return 'bundled';
  return 'resubscribe';
}
